// SPDX-License-Identifier: MIT
import { supabase } from './supabase';
import type { Provider } from '../components/picker';

// Computed lazily so unit tests with happy-dom can set window.location first.
function getCallbackUrl(): string {
  return `${window.location.origin}/auth/callback`;
}

export async function signInWithGoogle(providerPref: Provider): Promise<void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getCallbackUrl(),
      queryParams: { provider_preference: providerPref },
    },
  });
  if (error) throw error;
}

export async function signInWithMagicLink(email: string, providerPref: Provider): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: getCallbackUrl(),
      data: { provider_preference: providerPref },
    },
  });
  if (error) throw error;
}

// v1.1.0 — fallback for email-prefetch breakage of magic-link tokens.
// Some webmail clients (OVH webmail, corporate Outlook, antivirus link
// scanners) GET the magic-link verify URL once before the user clicks it
// — consuming the one-shot token. The user's actual click then returns
// `otp_expired` even within seconds of email arrival.
//
// The 6-digit OTP code in the same email is a SEPARATE artifact (not
// consumed by URL prefetch). Submitting it via this helper validates
// against GoTrue's stored OTP and yields a session directly — no
// `/auth/callback` round-trip needed. supabase-js's `verifyOtp` with
// `type: 'email'` is the canonical path.
export async function verifyEmailCode(email: string, code: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({
    email,
    token: code,
    type: 'email',
  });
  if (error) throw error;
}
